/**
 * ─────────────────────────────────────────────
 * src/services/organisationService.js
 * ─────────────────────────────────────────────
 * Admin-only organisation endpoints.
 *
 *   GET    /organisations                          list, newest first
 *   GET    /organisations/:id                      detail + projects + clients
 *   POST   /organisations                          create
 *   PATCH  /organisations/:id                      rename / edit description
 *   PATCH  /organisations/:id/projects             attach an existing project
 *   DELETE /organisations/:id/projects/:projectId  detach a project
 *   DELETE /organisations/:id                      delete (unlink | disable)
 *
 * Paths are written WITHOUT the "/api" prefix — the shared axios instance
 * supplies it via baseURL.
 */
import api from "../lib/api";

// ─────────────────────────────────────────────
// LIST ORGANISATIONS
// Returns: { success, data: [{ _id, name, projectCount, clientCount, ... }] }
// ─────────────────────────────────────────────
const getOrganisations = async () => {
  const response = await api.get("/organisations");
  return response.data;
};

// ─────────────────────────────────────────────
// GET ONE ORGANISATION (with its projects and clients)
// ─────────────────────────────────────────────
const getOrganisationById = async (organisationId) => {
  const response = await api.get(`/organisations/${organisationId}`);
  return response.data;
};

// ─────────────────────────────────────────────
// CREATE ORGANISATION
// Payload: { name, description? }
// ─────────────────────────────────────────────
const createOrganisation = async (payload) => {
  const response = await api.post("/organisations", payload);
  return response.data;
};

// ─────────────────────────────────────────────
// UPDATE ORGANISATION
// ─────────────────────────────────────────────
const updateOrganisation = async (organisationId, payload) => {
  const response = await api.patch(`/organisations/${organisationId}`, payload);
  return response.data;
};

// ─────────────────────────────────────────────
// ATTACH AN EXISTING PROJECT
// ─────────────────────────────────────────────
const addProjectToOrganisation = async (organisationId, projectId) => {
  const response = await api.patch(`/organisations/${organisationId}/projects`, {
    projectId,
  });
  return response.data;
};

// ─────────────────────────────────────────────
// DETACH A PROJECT (the project itself is untouched)
// ─────────────────────────────────────────────
const removeProjectFromOrganisation = async (organisationId, projectId) => {
  const response = await api.delete(
    `/organisations/${organisationId}/projects/${projectId}`,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// DELETE ORGANISATION
// mode: "unlink"  → projects stay active, just ungrouped
//       "disable" → projects are also disabled (reversible, nothing deleted)
// ─────────────────────────────────────────────
const deleteOrganisation = async (organisationId, mode = "unlink") => {
  const response = await api.delete(`/organisations/${organisationId}`, {
    data: { mode },
  });
  return response.data;
};

// ─────────────────────────────────────────────
// PROJECTS NOT YET IN ANY ORGANISATION
// Feeds the "add existing project" picker.
// ─────────────────────────────────────────────
const getUnassignedProjects = async () => {
  const response = await api.get("/projects?organisation=none");
  return response.data;
};

// ─────────────────────────────────────────────
// INVITE A CLIENT
// Reuses the normal invite endpoint. role is always "viewer" — the popup
// never shows a role picker, but Resources still displays it.
// ─────────────────────────────────────────────
const inviteClient = async (organisationId, { email, description, dateOfJoining }) => {
  const response = await api.post("/admin/users", {
    email,
    role: "viewer",
    description,
    dateOfJoining,
    organisation: organisationId,
  });
  return response.data;
};

// ─────────────────────────────────────────────
// VIEWER ACCOUNTS NOT YET IN THIS ORGANISATION
// Feeds the "add existing client" picker.
// ─────────────────────────────────────────────
const getAvailableClients = async (organisationId) => {
  const response = await api.get(
    `/organisations/${organisationId}/available-clients`,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// ATTACH AN EXISTING VIEWER AS A CLIENT
// ─────────────────────────────────────────────
const addExistingClient = async (organisationId, userId) => {
  const response = await api.patch(`/organisations/${organisationId}/clients`, {
    userId,
  });
  return response.data;
};

const organisationService = {
  getOrganisations,
  getAvailableClients,
  addExistingClient,
  getOrganisationById,
  createOrganisation,
  updateOrganisation,
  addProjectToOrganisation,
  removeProjectFromOrganisation,
  deleteOrganisation,
  getUnassignedProjects,
  inviteClient,
};

export default organisationService;
